import React, { useEffect, useState } from 'react';
import { Calendar, MapPin, Clock, Users } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import AnimatedSection from './AnimatedSection';
import { motion } from 'framer-motion';

const Events: React.FC = () => {
  const navigate = useNavigate();
  const [hostLocation, setHostLocation] = useState<string | null>(null);

  useEffect(() => {
    fetchHostLocation();
  }, []);

  const fetchHostLocation = async () => {
    // Check if Supabase is properly configured before attempting to fetch
    if (!isSupabaseConfigured()) {
      console.warn('Supabase is not configured. Using default host location.');
      setHostLocation('Contact us for location details');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('events')
        .select('host_location')
        .eq('is_recurring', true)
        .eq('status', 'approved')
        .limit(1);

      if (error) {
        console.warn('Error fetching host location from database:', error.message);
        setHostLocation('Contact us for location details');
        return;
      }
      
      setHostLocation(data?.[0]?.host_location || 'Contact us for location details');
    } catch (error) {
      console.warn('Network error fetching host location:', error);
      setHostLocation('Contact us for location details');
    }
  };

  const handleContactClick = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#contact');
    }
  };

  return (
    <section id="events" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <AnimatedSection className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Meeting Details</h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto mb-6"></div>
        </AnimatedSection>

        <div className="max-w-4xl mx-auto">
          <motion.div 
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <AnimatedSection delay={0.4}>
                  <motion.div 
                    className="flex items-start space-x-4"
                    whileHover={{ x: 10 }}
                  >
                    <Calendar className="text-blue-600 h-8 w-8 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-xl font-semibold text-gray-800">Every Week</h4>
                      <p className="text-gray-600">Join us for fellowship and study</p>
                    </div>
                  </motion.div>
                </AnimatedSection>
                
                <AnimatedSection delay={0.6}>
                  <motion.div 
                    className="flex items-start space-x-4"
                    whileHover={{ x: 10 }}
                  >
                    <Clock className="text-blue-600 h-8 w-8 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-xl font-semibold text-gray-800">2 Hours</h4>
                      <p className="text-gray-600">Focused meaningful connection</p>
                    </div>
                  </motion.div>
                </AnimatedSection>
              </div>
              
              <div className="space-y-6">
                <AnimatedSection delay={0.8}>
                  <motion.div 
                    className="flex items-start space-x-4"
                    whileHover={{ x: 10 }}
                  >
                    <MapPin className="text-blue-600 h-8 w-8 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-xl font-semibold text-gray-800">
                        {hostLocation || 'Contact us for location details'}
                      </h4>
                      <p className="text-gray-600">A welcoming space for our community</p>
                    </div>
                  </motion.div>
                </AnimatedSection>
                
                <AnimatedSection delay={1.0}>
                  <motion.div 
                    className="flex items-start space-x-4"
                    whileHover={{ x: 10 }}
                  >
                    <Users className="text-blue-600 h-8 w-8 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-xl font-semibold text-gray-800">Open to All Men</h4>
                      <p className="text-gray-600">Come as you are, all are welcome</p>
                    </div>
                  </motion.div>
                </AnimatedSection>
              </div>
            </div>

            <AnimatedSection delay={1.2} className="mt-12 text-center">
              <motion.button 
                onClick={handleContactClick}
                className="inline-block text-blue-600 hover:text-blue-800 font-medium transition-colors duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Contact us to learn more
              </motion.button>
            </AnimatedSection>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Events;